import { z } from 'zod';
import { ActingFor } from './acting-for-base.js';
import { Cents } from './common.js';
import { PoolPosition } from './awards.js';

const Ref = z.object({ id: z.string(), name: z.string() });
const Count = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const Year = z.number().int().min(2000).max(2100);
const Reason = z.string().trim().min(1).max(2000);

/** Each kind of ledger entry (K31): a move is two entries, one out of a pool and one into another. */
export const LedgerKind = z.enum([
  'initial',
  'addition',
  'reduction',
  'move_in',
  'move_out',
  'top_up',
]);
export type LedgerKind = z.infer<typeof LedgerKind>;

/**
 * A pool's newest live ledger entry for the year (APP-148, Home's "Latest change"): its kind, its
 * signed cash, when, who made it (null for MyRewards' own setup) and the Executive it was made for.
 */
export const PoolLatestChange = z.object({
  kind: LedgerKind,
  cashCents: Cents,
  at: z.iso.datetime({ offset: true }),
  by: Ref.nullable(),
  for: Ref.nullable(),
});
export type PoolLatestChange = z.infer<typeof PoolLatestChange>;

export const PoolPositions = z.object({
  fiscalYear: Year,
  pools: z.array(
    PoolPosition.extend({
      /** APP-148: null with nothing in the ledger this year. Optional: older servers omit it. */
      latest: PoolLatestChange.nullable().optional(),
    }),
  ),
});
export type PoolPositions = z.infer<typeof PoolPositions>;

/**
 * APP-148 R73: the second entry of one budget change, the other way (cash up and options down):
 * written with the first in one transaction, both or neither, for one record and one notice.
 */
export const AdjustBudgetAlso = z
  .object({
    kind: z.enum(['addition', 'reduction']),
    cashCents: Count,
    options: Count,
  })
  .strict()
  .refine((v) => v.cashCents > 0 || v.options > 0, 'A change needs cash or options');

/** X4: set a pool's budget for the year, or add to or take from it. Every change needs a reason. */
export const AdjustBudget = z
  .object({
    poolId: z.string().min(1),
    fiscalYear: Year,
    kind: z.enum(['initial', 'addition', 'reduction']),
    cashCents: Count,
    options: Count,
    reason: Reason,
    also: AdjustBudgetAlso.optional(),
    actingFor: ActingFor.optional(),
  })
  .strict()
  .refine((v) => v.cashCents > 0 || v.options > 0, 'A change needs cash or options')
  .refine(
    (v) => v.also === undefined || (v.kind !== 'initial' && v.also.kind !== v.kind),
    'The second entry must go the other way: an addition with a reduction',
  );
export type AdjustBudget = z.infer<typeof AdjustBudget>;

/** X4's second option: move budget from one pool to another (K31). */
export const MoveBudget = z
  .object({
    fromPoolId: z.string().min(1),
    toPoolId: z.string().min(1),
    fiscalYear: Year,
    cashCents: Count,
    options: Count,
    reason: Reason,
    actingFor: ActingFor.optional(),
  })
  .strict()
  .refine((v) => v.fromPoolId !== v.toPoolId, 'Choose two different pools')
  .refine((v) => v.cashCents > 0 || v.options > 0, 'A move needs cash or options');
export type MoveBudget = z.infer<typeof MoveBudget>;

/** One line of a pool's fiscal-year ledger (K31): what changed, who, why. */
export const LedgerEntryView = z.object({
  id: z.uuid(),
  kind: LedgerKind,
  cashCents: Cents,
  options: z.number().int(),
  reason: z.string().nullable(),
  /** Null for the deployment's system actor, "MyRewards setup" (APP-118). */
  by: Ref.nullable(),
  /** Who was signed in when `by` was acted as in Preview as (APP-122, APP-150); else null. */
  previewedBy: Ref.nullable().default(null),
  at: z.string(),
  /** A move's other pool. */
  otherPool: Ref.nullable(),
  /** A top-up's award. */
  award: z.object({ id: z.uuid(), displayId: z.string().nullable() }).nullable(),
  voided: z.boolean(),
  /** APP-148: the Executive this change was made for; `by` stays the Stock Admin who made it. */
  for: Ref.nullable().optional(),
  actedFor: z.object({ id: z.uuid(), hasNote: z.boolean(), files: z.int() }).nullable().optional(),
});
export const PoolLedger = z.object({
  pool: Ref,
  fiscalYear: Year,
  position: PoolPosition,
  entries: z.array(LedgerEntryView),
});
export type PoolLedger = z.infer<typeof PoolLedger>;

/** The ledger's kinds of change as X4 filters them: a move is one kind for both halves. */
export const LedgerChange = z.enum(['initial', 'addition', 'reduction', 'move', 'top_up']);
export type LedgerChange = z.infer<typeof LedgerChange>;

/**
 * `GET /budgets/ledger/export` (X4, APP-081): the fiscal-year ledger as CSV, narrowed the way the
 * screen is (one pool, one kind of change), with each entry's time in UTC, source and batch.
 */
export const LedgerExportQuery = z
  .object({
    fiscalYear: z.coerce.number().int().min(2000).max(2100),
    poolId: z.string().min(1).max(100).optional(),
    change: LedgerChange.optional(),
    /** APP-148 R74: only the entries a Stock Admin made for someone else. In a query string, "true". */
    doneForSomeone: z
      .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
      .optional(),
  })
  .strict();
export type LedgerExportQuery = z.infer<typeof LedgerExportQuery>;
